import React, { useMemo } from 'react';

import { SearchIcon, PlusIcon } from '@vlabs/icons';
import { Control, Divider } from '@vlabs/uikit';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { DEFAULT_GROUP_LABEL, OPTIONS_STEP } from '../../constants';
import { toggleOption, isSelected, normalizeGroupedOptions } from '../../helpers';
import { useFilteredOptions, useCheckboxFilterReducer } from '../../hooks';
import shared from '../../shared.module.sass';
import st from './CheckboxCustomFilter.module.sass';
import { AddOptionPanel } from './options/AddOptionPanel';
import { OptionItem } from './options/OptionItem';

export const CheckboxCustomFilter = ({
  control,
  name,
  label,
  options,
  isMulti = false,
  isCreatable = false,
  isSearchable = false,
  validateProp,
  onCreateOption,
  onDeleteOption,
  createdOptions = [],
}) => {
  const { t } = useTranslation();
  const [state, dispatch] = useCheckboxFilterReducer();
  const { searchValue, newOptionValue, newOptionError, isAdding } = state;

  const normalizedOptions = useMemo(() => normalizeGroupedOptions(options), [options]);

  const flatOptions = useMemo(
    () => normalizedOptions.flatMap((group) => group.options),
    [normalizedOptions],
  );

  const createdFlat = useMemo(
    () => createdOptions.flatMap((g) => g.options),
    [createdOptions],
  );

  const { visibleOptions, canShowMore, handleShowMore } = useFilteredOptions(
    normalizedOptions,
    searchValue,
    OPTIONS_STEP,
  );

  const shouldShowGroupLabels = useMemo(() => {
    return visibleOptions.some((group) => group.label !== DEFAULT_GROUP_LABEL);
  }, [visibleOptions]);

  const handleCreateOption = (fieldValue, onChange) => {
    const trimmed = newOptionValue.trim();
    if (!trimmed) return;

    const error = validateProp?.(trimmed);
    if (typeof error === 'string') {
      dispatch({ type: 'SET_NEW_OPTION_ERROR', payload: error });
      return;
    }

    const alreadyExists = flatOptions.some(
      (opt) => opt.value.toLowerCase() === trimmed.toLowerCase(),
    );
    if (alreadyExists) {
      dispatch({ type: 'SET_NEW_OPTION_ERROR', payload: t('value_already_exists') });
      return;
    }

    const newOption = { label: trimmed, value: trimmed };
    onCreateOption?.(newOption);

    if (isMulti) {
      onChange([...fieldValue, newOption]);
    } else {
      onChange(newOption);
    }
    dispatch({ type: 'CANCEL_ADDING' });
  };

  const handleNewOptionChange = (e) => {
    const inputValue = e.target.value;
    dispatch({ type: 'SET_NEW_OPTION_VALUE', payload: inputValue });

    if (inputValue.trim() === '') {
      dispatch({ type: 'SET_NEW_OPTION_ERROR', payload: '' });
      return;
    }

    const error = validateProp?.(inputValue.trim());
    dispatch({ type: 'SET_NEW_OPTION_ERROR', payload: typeof error === 'string' ? error : '' });
  };

  const isOption = (option) => !!option && typeof option === 'object' && ('value' in option || 'label' in option);

  return (
    <Controller
      control={control}
      defaultValue={isMulti ? [] : null}
      name={name}
      render={({ field }) => {
        const { value, onChange } = field;

        let safeOption;
        if (isMulti) {
          safeOption = Array.isArray(value) ? value.filter(isOption) : [];
        } else {
          safeOption = isOption(value) ? value : null;
        }

        const hasValue = isMulti
          ? safeOption.length > 0
          : isOption(safeOption);

        const handleReset = () => {
          field.onChange(isMulti ? [] : null);
        };

        const handleDeleteOption = (option) => {
          onDeleteOption?.(option);
          if (isMulti) {
            onChange(safeOption.filter((opt) => opt.value !== option.value));
          } else if (safeOption?.value === option.value) {
            onChange(null);
          }
        };

        return (
          <>
            {label && <label className="Subtitle-2">{label}</label>}
            <Divider small />

            {isSearchable && (
              <>
                <Control.Input
                  icon={<SearchIcon />}
                  isClearable
                  name={`${name}_search`}
                  onChange={(e) => dispatch({ type: 'SET_SEARCH_VALUE', payload: e.target.value })}
                  onClear={() => dispatch({ type: 'SET_SEARCH_VALUE', payload: '' })}
                  value={searchValue}
                />
                <Divider small />
              </>
            )}

            {visibleOptions.map((group) => (
              <div className={shared.wrapper_scrollable} key={group.label}>
                {shouldShowGroupLabels && (
                  <div className={st.GroupLabel} data-testid="OptionsGroupLabel">
                    {t(`${group.label}`)?.toUpperCase()}
                  </div>
                )}

                {group.options.map((option, index) => (
                  <OptionItem
                    checked={isSelected(safeOption, option, isMulti)}
                    isCreated={createdFlat.some((o) => o.value === option.value)}
                    isMulti={isMulti}
                    key={option.value || `option-${index}-${name}`}
                    name={name}
                    onChange={() => onChange(toggleOption(safeOption, option, isMulti))}
                    onDelete={handleDeleteOption}
                    option={option}
                  />
                ))}
              </div>
            ))}

            {isAdding && (
              <AddOptionPanel
                error={newOptionError}
                onCancel={() => dispatch({ type: 'CANCEL_ADDING' })}
                onChange={handleNewOptionChange}
                onCreate={() => handleCreateOption(safeOption, onChange)}
                value={newOptionValue}
              />
            )}

            <div className={st.PanelInLine}>
              {canShowMore && (
                <Control.Button
                  data-testid="ShowMore"
                  kind="primary"
                  onClick={handleShowMore}
                  size="small"
                  variant="flat"
                >
                  {t('btn.show_more')}
                </Control.Button>
              )}

              {isCreatable && !isAdding && (
                <Control.Button
                  data-testid="AddNewOption"
                  icon={<PlusIcon />}
                  kind="primary"
                  onClick={() => dispatch({ type: 'START_ADDING' })}
                  size="small"
                  variant="flat"
                >
                  {t('btn.add')}
                </Control.Button>
              )}
            </div>

            <div className={shared.Divider} />
            <Divider small />

            <Control.Button
              data-testid="ClearFilter"
              disabled={!hasValue}
              kind="negative"
              onClick={handleReset}
              size="small"
              variant="filled"
            >
              {t('button.clear')}
            </Control.Button>
          </>
        );
      }}
    />
  );
};
